import { logger } from "@/lib/logger";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Play, 
  Calendar, 
  ExternalLink, 
  Mail, 
  Clock,
  CheckCircle,
  BarChart3,
  TrendingUp,
  Zap,
  DollarSign,
  LogOut
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MEETING_LINK = "https://calendar.app.google/gbZSzz4TiFAYYSjL8";

export default function PendingApprovalPage({ user }) {
  const navigate = useNavigate();
  const [isDemoLoading, setIsDemoLoading] = useState(false);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    } catch (e) {
      logger.error("Logout error:", e);
    }
    window.location.href = "/login";
  };

  const handleQuickDemo = async () => {
    setIsDemoLoading(true);
    try {
      const response = await fetch(`${API}/auth/demo-session`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user?.email, name: user?.name }),
      });
      
      if (!response.ok) throw new Error("Failed to create demo session");
      const data = await response.json();
      localStorage.setItem("roas_session_token", data.token);
      setTimeout(() => { window.location.href = "/campaigns"; }, 100);
    } catch (error) {
      logger.error("Demo session error:", error);
      toast.error("Failed to start demo");
      setIsDemoLoading(false);
    }
  };

  const handleSignUp = () => {
    window.location.href = "/pricing-demo";
  };

  const features = [
    { icon: <BarChart3 className="w-5 h-5" />, title: "Cross-Platform Analytics" },
    { icon: <TrendingUp className="w-5 h-5" />, title: "ROAS Optimization" },
    { icon: <Zap className="w-5 h-5" />, title: "AI Budget Allocation" },
    { icon: <DollarSign className="w-5 h-5" />, title: "Revenue Tracking" },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        {/* Header Card */}
        <div className="bg-[#121212] border border-white/10 rounded-2xl p-8 mb-4">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-amber-400" />
            </div>
            
            <h1 className="font-heading text-2xl text-white font-bold mb-2">
              Account Pending Approval
            </h1>
            
            {user && (
              <div className="flex items-center justify-center gap-2 text-zinc-400 mb-4">
                <Mail className="w-4 h-4" />
                <span>{user.email}</span>
              </div>
            )}
            
            <p className="text-zinc-400 text-sm">
              Your account has been registered and is awaiting approval. 
              In the meantime, explore our demo to see what ROAS-AI can do for you.
            </p>
          </div>

          {/* Status Steps */}
          <div className="space-y-3 mb-8">
            <div className="flex items-center gap-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              <span className="text-emerald-300 text-sm">Account registered</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <Clock className="w-5 h-5 text-amber-400" />
              <span className="text-amber-300 text-sm">Pending admin approval</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-lg opacity-50">
              <Zap className="w-5 h-5 text-zinc-400" />
              <span className="text-zinc-400 text-sm">Full access unlocked</span>
            </div>
          </div>

          {/* Demo Button */}
          <Button
            data-testid="demo-access-btn"
            onClick={handleQuickDemo}
            disabled={isDemoLoading}
            className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold rounded-xl py-6 text-lg mb-3"
          >
            {isDemoLoading ? (
              <div className="spinner border-white border-t-transparent" />
            ) : (
              <>
                <Play className="w-5 h-5 mr-2 fill-current" />
                Explore Demo
              </>
            )}
          </Button>

          {/* Pricing / Subscribe */}
          <a href="/pricing-demo" className="block mb-3">
            <Button
              variant="outline"
              className="w-full border-violet-500/30 bg-violet-500/10 text-violet-400 hover:bg-violet-500/20 font-semibold rounded-xl py-5"
            >
              <Zap className="w-4 h-4 mr-2" />
              See Plans & Subscribe
            </Button>
          </a>

          {/* Logout */}
          <Button
            data-testid="pending-logout-btn"
            onClick={handleLogout}
            variant="outline"
            className="w-full border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20 font-semibold rounded-xl py-5 mb-4"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log Out
          </Button>
          
          <p className="text-zinc-500 text-xs text-center">
            You'll get full access once an admin approves your account
          </p>
        </div>

        {/* Features Preview */}
        <div className="bg-[#121212] border border-white/10 rounded-2xl p-6">
          <p className="text-zinc-400 text-xs uppercase tracking-wider mb-4">What you'll get access to:</p>
          <div className="grid grid-cols-2 gap-3">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.05 }}
                className="flex items-center gap-2 p-3 bg-white/5 rounded-lg"
              >
                <div className="text-zinc-400">{feature.icon}</div>
                <span className="text-zinc-400 text-sm">{feature.title}</span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col items-center gap-4 mt-6">
          <p className="text-zinc-500 text-xs">
            You'll be notified at {user?.email} once approved.
          </p>
        </div>
      </motion.div>
      <div className="fixed bottom-4 right-6">
        <p className="text-zinc-500 text-xs">Copyright (c) 2026 Beruf LLC</p>
      </div>
    </div>
  );
}
